import { Col } from '../../components/custom';

export default function EmployeeDisengagement()
{
    return (
        <Col id='employeeDisengagement' className='perspectives'>
            <Col className='banner'>
                <span className='title'>Diagnosing Declining Employee Engagement Through Hypothesis Testing</span>
            </Col>
            <Col className='section'>
                <span className='title'>The Challenge</span>
                <span className='desc bold'>Employee engagement scores are falling—but why?</span>
                <span className='descsmall'>Organizations often suspect factors like poor management, lack of growth opportunities, or burnout. Yet without statistical validation, these assumptions remain just that—assumptions. Reactive interventions based on intuition or anecdotes can lead to misdirected efforts, wasted resources, and further disengagement. What’s needed is a way to prove what’s actually driving the decline.</span>
            </Col>
            <Col className='section'>
                <span className='title'>Our Philosophy</span>
                <span className='desc bold'>Diagnose with data, not guesswork.</span>
                <span className='descsmall'>We believe meaningful improvements in engagement come from a rigorous understanding of what's truly affecting your workforce. Hypothesis testing provides that rigor—allowing us to validate or disprove suspected root causes of disengagement with statistical certainty. It's the difference between assuming and knowing.</span>
            </Col>
            <Col className='section'>
                <span className='title'>The Diagnostic Framework</span>
                <span className='desc'>We apply statistical hypothesis testing — such as <strong>t-tests, ANOVA, and Chi-squared tests</strong> to assess engagement patterns and test assumptions.</span>
                <ul>
                    <li className='descsmall'>
                        <strong>Comparing Groups</strong>
                        <ul>
                            <li className='descsmall'><strong>Goal:</strong> Identify disparities in survey scores across employee segments.</li>
                            <li className='descsmall'><strong>Method:</strong> Use a <strong>t-test</strong> to compare engagement scores between employees who received leadership training and those who didn't.</li>
                        </ul>
                    </li>
                    <li className='descsmall'>
                        <strong>Measuring Intervention Impact</strong>
                        <ul>
                            <li className='descsmall'><strong>Goal:</strong> Evaluate the effect of HR initiatives on engagement.</li>
                            <li className='descsmall'><strong>Method:</strong> Use a <strong>ANOVA</strong> to assess differences in scores across regions piloting different flexible work policies.</li>
                        </ul>
                    </li>
                    <li className='descsmall'>
                        <strong>Testing Categorical Relationships</strong>
                        <ul>
                            <li className='descsmall'><strong>Goal:</strong> Explore links between categorical variables and engagement.</li>
                            <li className='descsmall'><strong>Method:</strong> Use a <strong>Chi-squared test</strong> to see if low perceived growth opportunity is significantly associated with low engagement.</li>
                        </ul>
                    </li>
                    <li className='descsmall'>
                        <strong>Identifying Disparities</strong>
                        <ul>
                            <li className='descsmall'><strong>Goal:</strong> Pinpoint where declines are most acute.</li>
                            <li className='descsmall'><strong>Method:</strong> Analyze whether specific departments, tenures, or demographic groups are disproportionately disengaged.</li>
                        </ul>
                    </li>
                </ul>
            </Col>
            <Col className='section'>
                <span className='title'>What This Enables</span>
                <ul>
                    <li className='descsmall'><strong>Validation of Suspicions:</strong> Confirms whether suspected causes (e.g., workload, manager style) actually correlate with lower engagement.</li>
                    <li className='descsmall'><strong>Identification of Key Drivers:</strong> Reveals which variables show statistically significant differences.</li>
                    <li className='descsmall'><strong>Data-Driven Prioritization:</strong> Helps HR focus interventions on areas where the impact is real, not perceived.</li>
                    <li className='descsmall'><strong>Evidence-Based Decision Making:</strong> Moves HR toward analytics-driven credibility.</li>
                </ul>
            </Col>
            <Col className='section'>
                <span className='title'>The Takeaway</span>
                <span className='desc'>Hypothesis testing transforms employee engagement analysis from subjective to scientific. By statistically validating where and why engagement is declining, organizations can:</span>
                <ul>
                    <li className='descsmall'>Develop <strong>targeted interventions</strong> tailored to real pain points.</li>
                    <li className='descsmall'><strong>Optimize resources</strong> by focusing only on significant drivers.</li>
                    <li className='descsmall'>Improve <strong>employee experience and retention</strong> through focused solutions.</li>
                    <li className='descsmall'>Elevate <strong>HR credibility</strong> by bringing analytical precision to people decisions.</li>
                </ul>
            </Col>
        </Col>
    )
}